import { useState, useEffect, useLayoutEffect, RefObject, useRef } from 'react';

const MAX_LINES = 2;

// Use 'useLayoutEffect' on the client and 'useEffect' on SSR
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export function useAttributeTruncation(
  measureRef: RefObject<HTMLElement>,
  itemCount: number
) {
  const [cutoff, setCutoff] = useState<number>(itemCount);
  const lastWidth = useRef<number>(0);

  useIsomorphicLayoutEffect(() => {
    if (itemCount <= 1) {
      setCutoff(itemCount);
      return;
    }
    const measure = () => {
      const element = measureRef.current;
      if (!element) {
        return;
      }

      const currentWidth = element.getBoundingClientRect().width;
      if (currentWidth === 0) {
        return;
      }

      // Prevent flickering due to subpixels
      if (Math.abs(currentWidth - lastWidth.current) < 1) {
        return;
      }
      lastWidth.current = currentWidth;

      const spans = Array.from(
        element.querySelectorAll<HTMLSpanElement>('[data-m]')
      );
      const expandButton =
        element.querySelector<HTMLSpanElement>('[data-more]');
      if (!spans.length || !expandButton) {
        return;
      }

      // Calculate how many lines currently rendered
      const countRenderedLines = () => {
        const lineTops: number[] = [];
        spans.forEach((span) => {
          if (span.style.display !== 'none') {
            Array.from(span.getClientRects()).forEach((rect) =>
              lineTops.push(rect.top)
            );
          }
        });
        if (expandButton.style.display !== 'none') {
          Array.from(expandButton.getClientRects()).forEach((rect) =>
            lineTops.push(rect.top)
          );
        }

        if (lineTops.length === 0) {
          return 0;
        }
        lineTops.sort((a, b) => a - b);

        let lines = 1;
        let currentLineTop = lineTops[0];
        for (let i = 1; i < lineTops.length; i++) {
          // 10px tolerance in grouping rects
          if (lineTops[i] - currentLineTop > 10) {
            lines++;
            currentLineTop = lineTops[i];
          }
        }
        return lines;
      };

      // Check if list doesn't need truncation at all
      expandButton.style.display = 'none';
      spans.forEach((span) => (span.style.display = ''));

      if (countRenderedLines() <= MAX_LINES) {
        setCutoff(itemCount);
        return;
      }

      // Truncate; binary search to find max items that fit
      expandButton.style.display = 'inline';
      let low = 0;
      let high = spans.length;
      let bestCutoff = 0;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);

        for (let i = 0; i < spans.length; i++) {
          spans[i].style.display = i < mid ? '' : 'none';
        }

        if (countRenderedLines() <= MAX_LINES) {
          bestCutoff = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      spans.forEach((span) => (span.style.display = ''));
      expandButton.style.display = '';

      setCutoff(Math.max(1, bestCutoff));
    };

    measure();

    const element = measureRef.current;
    if (!element) return;

    let animationFrameId: number;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(measure);
    });

    resizeObserver.observe(element);
    return () => {
      cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, [itemCount, measureRef]);

  return cutoff;
}
